import { Box, Flex, GmgTheme, IconAddUser, SortButton, Tooltip } from '@gmg/gmg-react-components';
import { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppContext, { Role } from 'src/AppContext';
import useFormatter from 'src/customHooks/useFormatter';
import { ModuleId } from 'src/graphql/mappers/module';
import { ModuleLicense } from 'src/viewModels/Organization';
import { useTheme } from 'styled-components';
import UsersGraphicDark from '../../assets/svg/graphic_users-dark-255.svg';
import UsersGraphicLight from '../../assets/svg/graphic_users-light-255.svg';
import NoResultGraphicDark from '../../assets/svg/graphic_magnifier-dark-255.svg';
import NoResultGraphicLight from '../../assets/svg/graphic_magnifier-light-255.svg';
import useSort from '../../customHooks/useSort';
import { SortingOrder, Table, TableContainerCardStyle, Thead, Tr } from '../../shared/TableStyle';
import { User } from '../../viewModels/User';
import EmptyPage from '../shared/EmptyPage';
import { UserModalState } from './AddOrEditUserModal';
import AddOrEditUserModalContainer from './AddOrEditUserModalContainer';
import IconDisplayer from './IconDisplayer';
import InvitationStatusBadge, { InvitationStatus } from './InvitationStatusBadge';
import { AddOrEditUserSubmitData } from './UserManagement';
import UserManagementActions from './UserManagementActions';
import { LicenseSystem } from 'src/viewModels/LicenseSystem';

export interface UserManagementTableProps {
    orgId: string;
    users: User[];
    filterValue: string;
    maintainableRoles: Array<Role>;
    availableGroups: Map<string, string>;
    availableModules: Map<ModuleId, ModuleLicense>;
    onHandleEditUser: (
        userData: AddOrEditUserSubmitData,
        setUsermodalState: (props: UserModalState) => void,
        onClose: () => void
    ) => void;
    availableLicenseSystems: Array<LicenseSystem>;
}

const UserManagementTable: FC<UserManagementTableProps> = (props) => {
    const { onShowModal } = useContext(AppContext);
    const { t } = useTranslation();
    const theme: GmgTheme = useTheme();
    const { formatLastActivityDate, formatDateWithTime } = useFormatter();
    const [filteredUsers, setFilteredUsers] = useState<User[]>(props.users);
    const [sortingStatus, setSortingStatus] = useState<SortingOrder>({ direction: 'ASC', propertyName: 'name' });
    const { getSortFn, getSortStringsFn } = useSort();

    const sortFn = getSortFn(sortingStatus.propertyName, sortingStatus.direction);
    filteredUsers?.sort(sortFn);

    const sortLabelsInTableCell = getSortStringsFn('ASC');
    const handleSortButtonClick = (label: string) => {
        setSortingStatus({
            direction: sortingStatus.direction === 'ASC' ? 'DESC' : 'ASC',
            propertyName: label,
        });
    };

    useEffect(() => {
        setFilteredUsers(props.users.filter(({ firstName, lastName, email }) => {
            return [firstName?.toLowerCase(), lastName?.toLowerCase(), email.toLowerCase()].join(' ').includes(props.filterValue.toLowerCase());
        }));

    }, [props.users, props.filterValue]);

    const handleEditUserClick = (userId: string) => {
        onShowModal('inputModal', {
            title: t('UserManagementActions.editUser.modalTitle_title', 'Edit user'),
            titleIcon: <IconAddUser />,
            content: <AddOrEditUserModalContainer
                maintainableRoles={props.maintainableRoles}
                availableGroups={props.availableGroups}
                availableModules={props.availableModules}
                onHandleEditUser={props.onHandleEditUser}
                userId={userId}
                availableLicenseSystems={props.availableLicenseSystems}
            />,
        });
    };

    const iconEmptyPage = theme.type === 'dark'
        ? <UsersGraphicDark width={255} height={255} />
        : <UsersGraphicLight width={255} height={255} />;

    const iconNoResultPage = theme.type === 'dark'
        ? <NoResultGraphicDark width={255} height={255} />
        : <NoResultGraphicLight width={255} height={255} />;

    return (
        <TableContainerCardStyle className="vertical-scrollbar">
            <Table aria-label="Users">
                <Thead>
                    <Tr isSelected={false} className="px12_400_normal">
                        <th className="px12_600_normal">
                            <SortButton
                                buttonLabel={t('UserManagement.tableLabels.user_title', 'User')}
                                onClick={() => handleSortButtonClick('firstName')}
                                direction={sortingStatus.propertyName === 'firstName' ? sortingStatus.direction : undefined}
                            />
                        </th>
                        <th className="px12_600_normal">
                            <SortButton
                                buttonLabel={t('UserManagement.tableLabels.roles_title', 'Role')}
                                onClick={() => handleSortButtonClick('role')}
                                direction={sortingStatus.propertyName === 'role' ? sortingStatus.direction : undefined}
                            />
                        </th>
                        <th className="px12_600_normal">
                            <span>{t('UserManagement.tableLabels.modules_title', 'Modules')}</span>
                        </th>

                        <th className="px12_600_normal">
                            <span>{t('UserManagement.tableLabels.last_seen_title', 'Last seen')}</span>
                        </th>
                        <th className="px12_600_normal">
                            {/* The empty th is to fill the last item's place in the thead 'cos we have an extra td for action buttons */}
                        </th>
                    </Tr>
                </Thead>
                <tbody>
                    {props.users.length === 0
                        ? <tr>
                            <td>
                                <EmptyPage
                                    image={iconEmptyPage}
                                    title={t('EmptyUserPage.pageTitle_title', 'Invite users to the organization')}
                                    description={t('EmptyUserPage.page_description_desc', 'There are no users in this organization yet. All users that are invited will show up in this list with their user names and user rights.')}
                                />
                            </td>
                        </tr>
                        : filteredUsers.length === 0
                            ? <tr>
                                <td>
                                    <EmptyPage
                                        image={iconNoResultPage}
                                        title={t('NoSearchResultInUserManagement.pageTitle_title', 'No search results found')}
                                        description={t('NoSearchResultInUserManagement.page_description_desc', 'We could not find any results for your search. Please check your spelling or try using different keywords.')}
                                    />
                                </td>
                            </tr>
                            : filteredUsers.map(user => {
                                return (
                                    <Tr
                                        key={user.id}
                                        data-testid="row"
                                        isSelected={false}
                                        onClick={() => { handleEditUserClick(user.id); }}
                                        isClickable
                                    >
                                        <td className="px12_400_normal">
                                            <Flex gap='5px' flexDirection='column'>
                                                <Box>
                                                    {user.invite
                                                        ? <InvitationStatusBadge status={(user.invite ? user.invite.status : 'ACCEPTED') as InvitationStatus} />
                                                        : null
                                                    }
                                                    {user.firstName} {user.lastName}
                                                </Box>
                                                <span className="px10_400_weak">{user.email}</span>
                                            </Flex>
                                        </td>
                                        <td className="px12_400_normal">
                                            {t(`Roles.${user.role}_label`, user.role)}
                                        </td>
                                        <td className="px12_400_normal">
                                            <IconDisplayer moduleIds={user.assignedModuleIds} size={48} />
                                        </td>
                                        <td className="px12_400_normal">
                                            <Tooltip message={formatDateWithTime(user.lastActivityDate)}>
                                                <span>{formatLastActivityDate(user.lastActivityDate)}</span>
                                            </Tooltip>

                                        </td>
                                        <td className="actions">
                                            <UserManagementActions
                                                orgId={props.orgId}
                                                userId={user?.id}
                                                userFirstName={user?.firstName}
                                                userLastName={user?.lastName}
                                                userEmail={user.email}
                                                hasInvitation={!!user.invite}
                                                onEditUserIconClick={handleEditUserClick}
                                            />
                                        </td>
                                    </Tr>
                                );
                            })}
                </tbody>
            </Table>
        </TableContainerCardStyle>
    );
};

export default UserManagementTable;